import React, { useState, useRef, useEffect } from "react";
import { countries } from "country-data";

export default function CommonPhoneNumberInput({
  countryCode = "234",
  phoneNumber = "",
  onChange,
  placeholder = "940067966",
  className = "",
  disabled = false,
  error = false,
  onFocus = undefined,
  onBlur = undefined,
  maxLength = 11,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  const countryOptions = countries.all.filter(
    (country) => country.status === "assigned"
  );

  useEffect(() => {
    if (dropdownOpen) {
      searchInputRef.current?.focus();
    }
  }, [dropdownOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCountryChange = (newCode) => {
    onChange?.({ countryCode: newCode, phoneNumber });
    setDropdownOpen(false);
    setSearchQuery("");
    if (phoneInputRef?.current) {
      phoneInputRef.current.focus();
    }
  };

  const handlePhoneChange = (value) => {
    const sanitizedValue = value.replace(/\D/g, "");
    onChange?.({ phoneNumber: sanitizedValue, countryCode });
  };

  const filteredCountries = countryOptions.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.countryCallingCodes.some((code) => code.includes(searchQuery))
  );

  return (
    <div className={` ${className}`}>
      <div className="flex">
        <div className="" ref={dropdownRef}>
          <button
            disabled={disabled}
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={`px-3 h-12 items-center border ${
              error ? "border-red-600" : "border-gray-300"
            } bg-white text-gray-700 text-sm rounded-l-md focus:outline-none`}
          >
            +{countryCode}
          </button>
          {dropdownOpen && (
            <div className="absolute z-30 mt-2 bg-white border border-gray-300 shadow-md max-h-60 overflow-y-auto scroll-hide rounded-md w-64">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by country or code"
                ref={searchInputRef}
                className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:outline-none"
              />
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <div
                    key={country.alpha2}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                    onClick={() =>
                      handleCountryChange(
                        country.countryCallingCodes[0].replace("+", "")
                      )
                    }
                  >
                    {country.countryCallingCodes[0]} - {country.name}
                  </div>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="px-4 py-2 text-gray-500 text-sm">
                    No results found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <input
          type="text"
          value={phoneNumber}
          disabled={disabled}
          onChange={(e) => handlePhoneChange(e.target.value)}
          placeholder={placeholder}
          ref={phoneInputRef}
          onFocus={onFocus}
          onBlur={onBlur}
          maxLength={maxLength}
          className={`flex-1 px-3 h-12 items-center border ${
            error ? "border-red-600" : "border-gray-300"
          } rounded-r-md focus:outline-none text-sm`}
        />
      </div>
    </div>
  );
}
